import { Injectable } from "@angular/core";
import { AppwriteApiEndpoint, AppwriteApiRoute, C3AsMoldedBodyInventoryCount, C3AssemblyBomHardware, C3AssemblyGlobalState, C3ClearanceInventory, C3GlobalData, C3HardwareItem, C3HardwareLabelPrinterConfig, C3InventoryReportEmailConfig, C3InventoryThresholdReport, C3InventoryThresholdReportItem, C3Order, C3OrderedProduct, C3Product, C3ProductBillOfMaterialsLineItem, C3ProductBillOfMaterialsLineItemType, C3ProductType, C3ProductVersion, C3ProductVersionStatus, C3Report, C3RiderProductConfig, C3VendorHardwareData, Collection, EMPTY_C3_ASSEMBLY_GLOBAL_STATE, GlobalVariableDocumentIds, InventoryTransaction, InventoryTransactionType, User } from "@prism/prism-shared";
import { catchError, defaultIfEmpty, forkJoin, from, map, Observable, of, switchMap, throwError } from "rxjs";
import { SharedApiService } from './shared-api.service';
import { AppwriteDatabaseService, fromAppwriteDoc, fromAppwriteDocs, QueryRef } from '@prism/angular-shared';
import { AppwriteFunctionsService } from './appwrite-functions.service';

export interface GeneratedC3WeeklyInventoryReportResponse {
    fileName: string;
    contentBase64: string;
    generatedOn: string;
    totalBodies: number;
    totalLids: number;
    totalPieces: number;
}

export interface C3BomInventoryAdjustmentDictionaries {
    hardwareIdToQuantityChangeDict: Record<string, number>;
    productIdToQuantityChangeDict: Record<string, number>;
}

@Injectable({ providedIn: 'root' })
export class C3ApiService {
    constructor(
        private appwrite: AppwriteDatabaseService,
        private sharedApi: SharedApiService,
        private functions: AppwriteFunctionsService,
    ) { }

    private normalizeProductVersion(productVersion: C3ProductVersion): C3ProductVersion {
        return {
            ...productVersion,
            type: productVersion.type ?? C3ProductType.Finished,
        };
    }

    private normalizeProductVersions(productVersions: C3ProductVersion[]): C3ProductVersion[] {
        return productVersions.map((productVersion) => this.normalizeProductVersion(productVersion));
    }

    private addQuantityChange(dict: Record<string, number>, entityId: string, change: number): void {
        dict[entityId] = (dict[entityId] ?? 0) + change;
    }

    private loadReleasedProductVersionForBomAdjustment(
        productId: string,
        productVersionByProductId: Record<string, C3ProductVersion>,
    ): Observable<C3ProductVersion> {
        const productVersion = productVersionByProductId[productId];

        if (productVersion) {
            return of(this.normalizeProductVersion(productVersion));
        }

        return this.loadReleasedProductVersionByProductId(productId).pipe(
            map((releasedProductVersion) => {
                if (!releasedProductVersion) {
                    throw new Error(`No released C3 product version found for ${productId}`);
                }

                return releasedProductVersion;
            })
        );
    }

    private addComponentGroupBomInventoryAdjustments(
        componentGroupProductVersion: C3ProductVersion,
        parentChangeQty: number,
        productVersionByProductId: Record<string, C3ProductVersion>,
        hardwareIdToQuantityChangeDict: Record<string, number>,
        productIdToQuantityChangeDict: Record<string, number>,
        visitedProductIds: Set<string> = new Set(),
    ): Observable<void> {
        if (visitedProductIds.has(componentGroupProductVersion.productId)) {
            throw new Error(`Circular component group BOM reference found at ${componentGroupProductVersion.partNumber}`);
        }

        const nextVisitedProductIds = new Set(visitedProductIds);
        nextVisitedProductIds.add(componentGroupProductVersion.productId);

        return this.loadBillOfMaterialsLineItemsByProductVersionId(componentGroupProductVersion.id).pipe(
            switchMap((componentGroupBomRows) => {
                const adjustmentTasks = componentGroupBomRows.map((row) => {
                    const childChangeQty = parentChangeQty * row.quantity;

                    if (row.type === C3ProductBillOfMaterialsLineItemType.Hardware) {
                        this.addQuantityChange(
                            hardwareIdToQuantityChangeDict,
                            row.entityId,
                            childChangeQty * -1,
                        );
                        return of(undefined);
                    }

                    return this.loadReleasedProductVersionForBomAdjustment(row.entityId, productVersionByProductId).pipe(
                        switchMap((childProductVersion) => {
                            if (childProductVersion.type === C3ProductType.ComponentGroup) {
                                return this.addComponentGroupBomInventoryAdjustments(
                                    childProductVersion,
                                    childChangeQty,
                                    productVersionByProductId,
                                    hardwareIdToQuantityChangeDict,
                                    productIdToQuantityChangeDict,
                                    nextVisitedProductIds,
                                );
                            }

                            if (childProductVersion.type === C3ProductType.Finished) {
                                throw new Error(`${componentGroupProductVersion.partNumber} is a component group but contains finished product ${childProductVersion.partNumber}`);
                            }

                            this.addQuantityChange(
                                productIdToQuantityChangeDict,
                                row.entityId,
                                childChangeQty * -1,
                            );

                            return of(undefined);
                        })
                    );
                });

                return forkJoin(adjustmentTasks).pipe(
                    defaultIfEmpty([]),
                    map(() => undefined),
                );
            })
        );
    }

    public setHardwareItem(hardwareItem: C3HardwareItem): Observable<void> {
        return from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(hardwareItem.id).set(hardwareItem))
    }

    public updateHardwareItemStockLimits(
        hardwareItemId: string,
        minimumStock: number | null,
        maximumStock: number | null,
        currentUserId: string,
    ): Observable<void> {
        return from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(hardwareItemId).update({
            minimumStock,
            maximumStock,
            lastSavedOn: new Date(),
            lastSavedbyId: currentUserId,
        }));
    }

    public updateHardwareItemStockLimitEnabled(
        hardwareItemId: string,
        stockLimitEnabled: boolean,
        currentUserId: string,
    ): Observable<void> {
        return from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(hardwareItemId).update({
            stockLimitEnabled,
            lastSavedOn: new Date(),
            lastSavedbyId: currentUserId,
        }));
    }

    public loadHardwareItems(): Observable<C3HardwareItem[]> {
        return from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public watchHardwareItems(): Observable<C3HardwareItem[]> {
        return this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).valueChanges();
    }

    public loadHardwareItemById(hardwareItemId: string): Observable<C3HardwareItem> {
        return from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(hardwareItemId).get().pipe(
            map((resp) => fromAppwriteDoc(resp))
        ))
    }

    public loadHardwareItemsByIds(hardwareItemIds: string[]): Observable<C3HardwareItem[]> {
        return this.appwrite.listByIds<C3HardwareItem>(Collection.c3HardwareItem, hardwareItemIds);
    }

    public loadC3HardwareLabelPrinterConfig(): Observable<C3HardwareLabelPrinterConfig> {
        return this.loadC3GlobalData().pipe(
            map(({ labelPrinterId }) => ({ labelPrinterId })),
        );
    }

    public loadC3RiderProductConfig(): Observable<C3RiderProductConfig> {
        return from(this.appwrite.collection<C3RiderProductConfig>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.C3RiderProductId)
            .get()
            .pipe(map((resp) => fromAppwriteDoc(resp))));
    }

    public loadC3ClearanceInventory(): Observable<C3ClearanceInventory | null> {
        return this.appwrite.collection<C3ClearanceInventory>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.C3ClearanceInventory)
            .get()
            .pipe(map((resp) => fromAppwriteDoc(resp) ?? null));
    }

    public saveC3ClearanceInventory(inventory: C3ClearanceInventory): Observable<void> {
        return from(this.appwrite.collection<C3ClearanceInventory>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.C3ClearanceInventory)
            .set(inventory));
    }

    public loadC3WeeklyInventoryReportEmailConfig(): Observable<C3InventoryReportEmailConfig> {
        return this.appwrite.collection<C3InventoryReportEmailConfig>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.C3InventoryReportEmailConfig)
            .get()
            .pipe(map((resp) => {
                const config = fromAppwriteDoc<C3InventoryReportEmailConfig>(resp);
                return config ?? { recipientUserIds: [], additionalRecipientEmails: [] };
            }));
    }

    public saveC3WeeklyInventoryReportEmailConfig(config: C3InventoryReportEmailConfig): Observable<void> {
        return from(this.appwrite.collection<C3InventoryReportEmailConfig>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.C3InventoryReportEmailConfig)
            .set(config));
    }

    public generateC3WeeklyInventoryReport(): Observable<GeneratedC3WeeklyInventoryReportResponse> {
        return this.functions.post<GeneratedC3WeeklyInventoryReportResponse>(
            'api',
            `/${AppwriteApiRoute.C3Reporting}/${AppwriteApiEndpoint.GenerateC3WeeklyInventoryReport}`,
            {},
        );
    }

    public sendTestC3WeeklyInventoryReport(): Observable<{ recipientCount: number; accepted: string[]; fileName: string }> {
        return this.functions.post<{ recipientCount: number; accepted: string[]; fileName: string }>(
            'api',
            `/${AppwriteApiRoute.C3Reporting}/${AppwriteApiEndpoint.SendTestC3WeeklyInventoryReport}`,
            {},
        );
    }

    public loadC3GlobalData(): Observable<C3GlobalData> {
        return from(this.appwrite.collection<C3GlobalData>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.C3HardwareLabelPrinterId)
            .get()
            .pipe(map((resp) => fromAppwriteDoc(resp) ?? { labelPrinterId: '' })));
    }

    public saveC3AsMoldedBodyInventoryCount(
        count: C3AsMoldedBodyInventoryCount,
    ): Observable<void> {
        return this.loadC3GlobalData().pipe(
            switchMap((current) => from(
                this.appwrite.collection<C3GlobalData>(Collection.globalVariables)
                    .doc(GlobalVariableDocumentIds.C3HardwareLabelPrinterId)
                    .set({
                        ...current,
                        asMoldedBodyInventoryCount: count,
                    }),
            )),
        );
    }

    public loadRecentHardwareInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.C3Hardware);
    }

    public getInventoryTransactionsByHardwareItemId(hardwareItemId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityId(hardwareItemId, InventoryTransactionType.C3Hardware);
    }

    public getHighestHardwareItemPartNumber(): Observable<number> {
        return this.loadHardwareItems().pipe(
            map((hardwareItems) => {
                const partNumbers = hardwareItems
                    .map((item) => item.partNumber)
                    .map((partNumber) => parseInt(partNumber.split('-')[1]))
                const sortedPartNumbers = partNumbers.sort((a, b) => b - a)
                return sortedPartNumbers[0] || 0
            })
        )
    }

    public loadVendorHardwareData(): Observable<C3VendorHardwareData[]> {
        return from(this.appwrite.collection<C3VendorHardwareData>(Collection.c3VendorHardwareData).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public loadVendorHardwareDataByHardwareItemId(hardwareItemId: string): Observable<C3VendorHardwareData[]> {
        return from(this.appwrite.collection<C3VendorHardwareData>(
            Collection.c3VendorHardwareData,
            ref => ref.where('parentHardwareItemId', '==', hardwareItemId)
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public loadVendorHardwareDataByHardwareItemIds(hardwareItemIds: string[]): Observable<C3VendorHardwareData[]> {
        return this.appwrite.listByFieldValues<C3VendorHardwareData>(Collection.c3VendorHardwareData, 'parentHardwareItemId', hardwareItemIds);
    }

    public loadReports(): Observable<C3Report[]> {
        return from(this.appwrite.collection<C3Report>(
            Collection.c3Report,
            ref => ref.orderBy('createdOn', 'desc')
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ));
    }

    public loadReportById(reportId: string): Observable<C3Report> {
        return from(this.appwrite.collection<C3Report>(Collection.c3Report).doc(reportId).get().pipe(
            map((resp) => fromAppwriteDoc(resp))
        ));
    }

    public loadInventoryThresholdReportById(reportId: string): Observable<C3InventoryThresholdReport> {
        return from(this.appwrite.collection<C3InventoryThresholdReport>(Collection.c3InventoryThresholdReport).doc(reportId).get().pipe(
            map((resp) => fromAppwriteDoc(resp))
        ));
    }

    public loadInventoryThresholdReportItems(reportId: string): Observable<C3InventoryThresholdReportItem[]> {
        return from(this.appwrite.collection<C3InventoryThresholdReportItem>(
            Collection.c3InventoryThresholdReportItem,
            ref => ref.where('reportId', '==', reportId)
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ));
    }

    public loadAssemblyInventoryTransactions(taskId: string): Observable<InventoryTransaction[]> {
        return from(this.appwrite.collection<InventoryTransaction>(
            Collection.inventoryTransaction,
            ref => ref.where('processId', '==', taskId)
        ).get().pipe(
            map((resp) => fromAppwriteDocs<InventoryTransaction>(resp.docs)
                .sort((left, right) => left.createdOn.getTime() - right.createdOn.getTime()))
        ));
    }

    public watchAssemblyState(): Observable<C3AssemblyGlobalState> {
        return this.appwrite.collection<C3AssemblyGlobalState>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.C3AssemblyState)
            .valueChanges()
            .pipe(map((state) => state ?? { ...EMPTY_C3_ASSEMBLY_GLOBAL_STATE }));
    }

    public createAssemblyCommandId(): string {
        return this.appwrite.createId();
    }

    public newHardwareInventoryTransaction(
        hardwareItemId: string,
        changeQty: number,
        comment: string,
        curentUser: User,
        processId: string | null = null,
    ): Observable<InventoryTransaction> {

        const transaction: InventoryTransaction = {
            id: this.appwrite.createId(),
            entityId: hardwareItemId,
            change: changeQty,
            comment,
            createdOn: new Date(),
            createdById: curentUser?.id || "",
            type: InventoryTransactionType.C3Hardware,
            ...(processId ? { processId } : {}),
        }
        transaction.transactionGroupId = transaction.id;

        const hardwareItemUpdate$: Observable<void> = this.loadHardwareItemById(hardwareItemId).pipe(
            switchMap(item => from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(hardwareItemId).update({
                qtyOnHand: (item.qtyOnHand ?? 0) + changeQty,
            })))
        );

        const transactionCreate$: Observable<void> = this.sharedApi.setInventoryTransaction(transaction);

        return forkJoin([hardwareItemUpdate$, transactionCreate$]).pipe(
            map(() => transaction)
        )
    }

    public setVendorHardwareData(vendorHardwareData: C3VendorHardwareData): Observable<void> {
        return from(this.appwrite.collection<C3VendorHardwareData>(Collection.c3VendorHardwareData).doc(vendorHardwareData.id).set(vendorHardwareData))
    }

    public loadProducts(): Observable<C3Product[]> {
        return from(this.appwrite.collection<C3Product>(Collection.c3Product).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public watchProducts(): Observable<C3Product[]> {
        return this.appwrite.collection<C3Product>(Collection.c3Product).valueChanges();
    }

    public loadReleasedAndDraftProductVersions(): Observable<C3ProductVersion[]> {
        return from(this.appwrite.collection<C3ProductVersion>(Collection.c3ProductVersion,
            ref => ref.where('status', 'in', [C3ProductVersionStatus.Released, C3ProductVersionStatus.Draft])
        ).get()).pipe(
            map((resp) => this.normalizeProductVersions(fromAppwriteDocs(resp.docs)))
        )
    }

    public setProduct(product: C3Product): Observable<void> {
        return from(this.appwrite.collection<C3Product>(Collection.c3Product).doc(product.id).set(product))
    }

    public setProductVersion(productVersion: C3ProductVersion): Observable<void> {
        return from(this.appwrite.collection<C3ProductVersion>(Collection.c3ProductVersion).doc(productVersion.id).set(this.normalizeProductVersion(productVersion)))
    }

    public loadProductById(productId: string): Observable<C3Product> {
        return from(this.appwrite.collection<C3Product>(Collection.c3Product).doc(productId).get().pipe(
            map((resp) => fromAppwriteDoc(resp))
        ))
    }

    public loadProductsByIds(productIds: string[]): Observable<C3Product[]> {
        return this.appwrite.listByIds<C3Product>(Collection.c3Product, productIds);
    }

    public loadProductVersionsByProductId(productId: string): Observable<C3ProductVersion[]> {
        return from(this.appwrite.collection<C3ProductVersion>(
            Collection.c3ProductVersion,
            ref => ref.where('productId', '==', productId)
        ).get().pipe(
            map((resp) => this.normalizeProductVersions(fromAppwriteDocs(resp.docs)))
        ))
    }

    public loadBillOfMaterialsLineItemsByProductVersionId(productVersionId: string): Observable<C3ProductBillOfMaterialsLineItem[]> {
        return from(this.appwrite.collection<C3ProductBillOfMaterialsLineItem>(
            Collection.c3ProductBillOfMaterialsLineItem,
            ref => ref.where('productVersionId', '==', productVersionId)
        ).get()).pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        )
    }

    public loadReleasedProductVersions(): Observable<C3ProductVersion[]> {
        return from(this.appwrite.collection<C3ProductVersion>(
            Collection.c3ProductVersion,
            ref => ref.where('status', '==', C3ProductVersionStatus.Released)
        ).get().pipe(
            map((resp) => this.normalizeProductVersions(fromAppwriteDocs(resp.docs)))
        ))
    }

    public loadReleasedProductVersionByProductId(productId: string): Observable<C3ProductVersion> {
        const query = (ref: QueryRef) => ref
            .where('status', '==', C3ProductVersionStatus.Released)
            .where('productId', '==', productId)
        return from(this.appwrite.collection<C3ProductVersion>(Collection.c3ProductVersion, query).get()).pipe(
            map(resp => {
                if (!resp.docs.length) {
                    return null as unknown as C3ProductVersion
                }
                return this.normalizeProductVersion(fromAppwriteDoc(resp.docs[0]))
            })
        )
    }

    public setBillOfMaterialsLineItem(lineItem: C3ProductBillOfMaterialsLineItem): Observable<void> {
        return from(this.appwrite.collection<C3ProductBillOfMaterialsLineItem>(Collection.c3ProductBillOfMaterialsLineItem).doc(lineItem.id).set(lineItem))
    }

    public setBillOfMaterialsLineItems(lineItems: C3ProductBillOfMaterialsLineItem[]): Observable<void[]> {
        return forkJoin(lineItems.map((li) => this.setBillOfMaterialsLineItem(li))).pipe(
            defaultIfEmpty([])
        )
    }

    public loadProductVersionById(versionId: string): Observable<C3ProductVersion> {
        return from(this.appwrite.collection<C3ProductVersion>(Collection.c3ProductVersion).doc(versionId).get().pipe(
            map((resp) => this.normalizeProductVersion(fromAppwriteDoc(resp)))
        ))
    }

    public loadProductVersionsByIds(productVersionIds: string[]): Observable<C3ProductVersion[]> {
        return this.appwrite.listByIds<C3ProductVersion>(Collection.c3ProductVersion, productVersionIds).pipe(
            map((productVersions) => this.normalizeProductVersions(productVersions))
        );
    }

    public deleteBillOfMaterialsLineItemsByIds(lineItemIds: string[]): Observable<void[]> {
        const lineItems$ = lineItemIds.map(lineItemId => from(
            this.appwrite.collection<C3ProductBillOfMaterialsLineItem>(
                Collection.c3ProductBillOfMaterialsLineItem,
            ).doc(lineItemId).delete()).pipe(
                catchError((error) => {
                    if (error?.code === 404 || String(error?.message ?? '').toLowerCase().includes('not found')) {
                        return of(undefined);
                    }

                    throw error;
                })
            ));
        return forkJoin(lineItems$).pipe(
            defaultIfEmpty([])
        )
    }

    public deleteProductVersion(versionId: string): Observable<void> {
        return from(this.appwrite.collection<C3ProductVersion>(Collection.c3ProductVersion).doc(versionId).delete())
    }

    public getReleasedProductVersionCount(productId: string) {
        const query = (ref: QueryRef) => ref
            .where('productId', '==', productId)
            .where('status', 'in', [C3ProductVersionStatus.Released, C3ProductVersionStatus.Obsolete])
        return from(this.appwrite.collection(Collection.c3ProductVersion, query).get()).pipe(
            map((resp) => resp.docs.length)
        );
    }

    public setCurrentReleasedProductVersionToObsolete(productId: string) {
        const query = (ref: QueryRef) => ref
            .where('productId', '==', productId)
            .where('status', '==', C3ProductVersionStatus.Released)
        return from(this.appwrite.collection<C3ProductVersion>(Collection.c3ProductVersion, query).get()).pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
            switchMap((productVersions) => {
                const updates$ = productVersions.map(v => this.appwrite.collection(Collection.c3ProductVersion).doc(v.id).update({
                    status: C3ProductVersionStatus.Obsolete
                }))
                return forkJoin(updates$).pipe(
                    defaultIfEmpty([])
                )
            })
        );
    }

    public getInventoryTransactionsByProductId(productId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityId(productId, InventoryTransactionType.C3Product);
    }

    public newProductInventoryTransaction(
        productId: string,
        changeQty: number,
        comment: string,
        hardwareItemIdToQuantityChangeDict: Record<string, number>,
        productIdToQuantityChangeDict: Record<string, number>,
        curentUser: User,
        processId: string | null = null,
    ): Observable<InventoryTransaction> {

        const hasBomAdjustments = [
            ...Object.values(hardwareItemIdToQuantityChangeDict),
            ...Object.values(productIdToQuantityChangeDict),
        ].some((change) => change !== 0);
        if (changeQty < 0 && hasBomAdjustments) {
            return throwError(() => new Error(
                'Removing a finished C3 product cannot adjust BOM inventory. Use a separate disassembly transaction.',
            ));
        }

        const transaction: InventoryTransaction = {
            id: this.appwrite.createId(),
            entityId: productId,
            change: changeQty,
            comment,
            createdOn: new Date(),
            createdById: curentUser?.id || "",
            type: InventoryTransactionType.C3Product,
            ...(processId ? { processId } : {}),
        }
        transaction.transactionGroupId = transaction.id;

        const productUpdate$: Observable<void> = this.loadProductById(productId).pipe(
            switchMap(product => from(this.appwrite.collection<C3Product>(Collection.c3Product).doc(productId).update({
                qtyOnHand: (product.qtyOnHand ?? 0) + changeQty,
            })))
        );

        const transactionCreate$: Observable<void> = this.sharedApi.setInventoryTransaction(transaction);

        const hardwareItemUpdates$: Observable<void>[] = Object.entries(hardwareItemIdToQuantityChangeDict).map(([hardwareItemId, change]) => {
            return this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem)
                .doc(hardwareItemId).get().pipe(
                    map((resp) => {
                        const item: C3HardwareItem = fromAppwriteDoc(resp);
                        item.qtyOnHand += change
                        return item;
                    }),
                    switchMap((item) => from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(item.id).set(item)))
                )
        })

        const productUpdates$: Observable<void>[] = Object.entries(productIdToQuantityChangeDict).map(([productId, change]) => {
            return this.loadProductById(productId).pipe(
                switchMap(product => from(this.appwrite.collection<C3Product>(Collection.c3Product).doc(productId).update({
                    qtyOnHand: (product.qtyOnHand ?? 0) + change,
                })))
            );
        })

        const hardwareItemTransactions$: Observable<void>[] = Object.entries(hardwareItemIdToQuantityChangeDict).map(([hardwareItemId, change]) => {
            return this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(hardwareItemId).get().pipe(
                switchMap(resp => {
                    const hardwareTransaction: InventoryTransaction = {
                        id: this.appwrite.createId(),
                        entityId: hardwareItemId,
                        change,
                        comment,
                        createdOn: new Date(),
                        createdById: curentUser?.id || "",
                        type: InventoryTransactionType.C3Hardware,
                        transactionGroupId: transaction.id,
                        ...(processId ? { processId } : {}),
                    }
                    return this.sharedApi.setInventoryTransaction(hardwareTransaction);
                })
            )
        })
        const productTransactions$: Observable<InventoryTransaction>[] = Object.entries(productIdToQuantityChangeDict).map(([productId, change]) => {
            const productTransaction: InventoryTransaction = {
                id: this.appwrite.createId(),
                entityId: productId,
                change,
                comment,
                createdOn: new Date(),
                createdById: curentUser?.id || "",
                type: InventoryTransactionType.C3Product,
                transactionGroupId: transaction.id,
                ...(processId ? { processId } : {}),
            }
            return this.sharedApi.setInventoryTransaction(productTransaction).pipe(
                map(() => productTransaction)
            )
        })

        return forkJoin([
            productUpdate$,
            transactionCreate$,
            ...hardwareItemUpdates$,
            ...productUpdates$,
            ...hardwareItemTransactions$,
            ...productTransactions$,
        ]).pipe(
            map(() => transaction)
        )

    }

    public newComponentGroupBomInventoryTransaction(
        comment: string,
        hardwareItemIdToQuantityChangeDict: Record<string, number>,
        productIdToQuantityChangeDict: Record<string, number>,
        currentUser: User,
    ): Observable<InventoryTransaction[]> {
        const hardwareChanges = Object.entries(hardwareItemIdToQuantityChangeDict)
            .filter(([, change]) => change !== 0);
        const productChanges = Object.entries(productIdToQuantityChangeDict)
            .filter(([, change]) => change !== 0);
        const allChanges = [...hardwareChanges, ...productChanges];

        if (!allChanges.length) {
            return throwError(() => new Error('This component group has no inventory-tracked BOM items'));
        }

        if (allChanges.some(([, change]) => !Number.isFinite(change) || change >= 0)) {
            return throwError(() => new Error('Component group transactions may only decrement BOM inventory'));
        }

        const transactionGroupId = this.appwrite.createId();
        const createdOn = new Date();

        const hardwareTransactions: InventoryTransaction[] = hardwareChanges.map(([hardwareItemId, change]) => ({
            id: this.appwrite.createId(),
            entityId: hardwareItemId,
            change,
            comment,
            createdOn,
            createdById: currentUser?.id || '',
            type: InventoryTransactionType.C3Hardware,
            transactionGroupId,
        }));
        const productTransactions: InventoryTransaction[] = productChanges.map(([productId, change]) => ({
            id: this.appwrite.createId(),
            entityId: productId,
            change,
            comment,
            createdOn,
            createdById: currentUser?.id || '',
            type: InventoryTransactionType.C3Product,
            transactionGroupId,
        }));
        const transactions = [...hardwareTransactions, ...productTransactions];

        const hardwareItemUpdates$ = hardwareChanges.map(([hardwareItemId, change]) =>
            this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem).doc(hardwareItemId).get().pipe(
                map((resp) => fromAppwriteDoc<C3HardwareItem>(resp)),
                switchMap((item) => from(this.appwrite.collection<C3HardwareItem>(Collection.c3HardwareItem)
                    .doc(item.id)
                    .update({ qtyOnHand: (item.qtyOnHand ?? 0) + change })))
            )
        );
        const productUpdates$ = productChanges.map(([productId, change]) =>
            this.loadProductById(productId).pipe(
                switchMap((product) => from(this.appwrite.collection<C3Product>(Collection.c3Product)
                    .doc(productId)
                    .update({ qtyOnHand: (product.qtyOnHand ?? 0) + change })))
            )
        );
        const transactionCreates$ = transactions.map((transaction) =>
            this.sharedApi.setInventoryTransaction(transaction)
        );

        return forkJoin([
            ...hardwareItemUpdates$,
            ...productUpdates$,
            ...transactionCreates$,
        ]).pipe(
            map(() => transactions),
        );
    }

    public buildBomInventoryAdjustmentDictionaries(
        changeQty: number,
        hardwareLineItems: C3ProductBillOfMaterialsLineItem[],
        productLineItems: C3ProductBillOfMaterialsLineItem[],
        productVersionByProductId: Record<string, C3ProductVersion>,
    ): Observable<C3BomInventoryAdjustmentDictionaries> {
        const hardwareIdToQuantityChangeDict: Record<string, number> = {};
        const productIdToQuantityChangeDict: Record<string, number> = {};

        hardwareLineItems.forEach((lineItem) => {
            this.addQuantityChange(
                hardwareIdToQuantityChangeDict,
                lineItem.entityId,
                changeQty * lineItem.quantity * -1,
            );
        });

        const productAdjustmentTasks = productLineItems.map((lineItem) => {
            return this.loadReleasedProductVersionForBomAdjustment(lineItem.entityId, productVersionByProductId).pipe(
                switchMap((productVersion) => {
                    const childChangeQty = changeQty * lineItem.quantity;

                    if (productVersion.type === C3ProductType.ComponentGroup) {
                        return this.addComponentGroupBomInventoryAdjustments(
                            productVersion,
                            childChangeQty,
                            productVersionByProductId,
                            hardwareIdToQuantityChangeDict,
                            productIdToQuantityChangeDict,
                        );
                    }

                    this.addQuantityChange(
                        productIdToQuantityChangeDict,
                        lineItem.entityId,
                        childChangeQty * -1,
                    );

                    return of(undefined);
                })
            );
        });

        return forkJoin(productAdjustmentTasks).pipe(
            defaultIfEmpty([]),
            map(() => ({ hardwareIdToQuantityChangeDict, productIdToQuantityChangeDict }))
        );
    }

    public loadBillOfMaterialsLineItemsByEntityId(entityId: string): Observable<C3ProductBillOfMaterialsLineItem[]> {
        return from(this.appwrite.collection<C3ProductBillOfMaterialsLineItem>(
            Collection.c3ProductBillOfMaterialsLineItem,
            ref => ref.where('entityId', '==', entityId)
        ).get()).pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        )
    }

    private collectResolvedAssemblyHardwareBom(
        productVersion: C3ProductVersion,
        multiplier: number,
        quantities: Record<string, number>,
        visitedProductIds: Set<string>,
    ): Observable<void> {
        if (visitedProductIds.has(productVersion.productId)) {
            throw new Error(`Circular component group BOM reference found at ${productVersion.partNumber}`);
        }
        const nextVisited = new Set(visitedProductIds);
        nextVisited.add(productVersion.productId);

        return this.loadBillOfMaterialsLineItemsByProductVersionId(productVersion.id).pipe(
            switchMap((rows) => forkJoin(rows.map((row) => {
                const quantity = multiplier * row.quantity;
                if (row.type === C3ProductBillOfMaterialsLineItemType.Hardware) {
                    this.addQuantityChange(quantities, row.entityId, quantity);
                    return of(undefined);
                }
                return this.loadReleasedProductVersionByProductId(row.entityId).pipe(
                    switchMap((childVersion) => {
                        if (!childVersion) {
                            throw new Error(`No released C3 product version found for ${row.entityId}`);
                        }
                        return childVersion.type === C3ProductType.ComponentGroup
                            ? this.collectResolvedAssemblyHardwareBom(childVersion, quantity, quantities, nextVisited)
                            : of(undefined);
                    }),
                );
            })).pipe(
                defaultIfEmpty([]),
                map(() => undefined),
            )),
        );
    }

    public loadResolvedAssemblyHardwareBom(productVersionId: string): Observable<C3AssemblyBomHardware[]> {
        return this.loadProductVersionById(productVersionId).pipe(
            switchMap((productVersion) => {
                const quantities: Record<string, number> = {};
                return this.collectResolvedAssemblyHardwareBom(productVersion, 1, quantities, new Set()).pipe(
                    map(() => Object.entries(quantities)
                        .map(([hardwareItemId, quantityPerProduct]) => ({ hardwareItemId, quantityPerProduct }))
                        .sort((a, b) => a.hardwareItemId.localeCompare(b.hardwareItemId))),
                );
            }),
        );
    }

    public loadRecentProductInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.C3Product);
    }


    // ===================== orders =====================
    // C3 orders are a header + lines only: no ship-to, no tasks. Status lives on the
    // line (C3OrderedProduct.status), so there is nothing order-level to query by.

    public loadOrders(): Observable<C3Order[]> {
        return this.appwrite.collection<C3Order>(Collection.c3Order).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public watchOrders(): Observable<C3Order[]> {
        return this.appwrite.collection<C3Order>(Collection.c3Order).valueChanges();
    }

    public loadOrderById(orderId: string): Observable<C3Order> {
        return this.appwrite.collection<C3Order>(Collection.c3Order).doc(orderId).get().pipe(
            map((resp) => fromAppwriteDoc(resp)),
        );
    }

    public watchOrderById(orderId: string): Observable<C3Order> {
        return this.appwrite.collection<C3Order>(Collection.c3Order).doc(orderId).valueChanges().pipe(
            map((order) => order || null as unknown as C3Order),
        );
    }

    public loadOrdersByIds(orderIds: string[]): Observable<C3Order[]> {
        return this.appwrite.listByIds<C3Order>(Collection.c3Order, orderIds);
    }

    public getOrderCount(): Observable<number> {
        return this.appwrite.collection<C3Order>(Collection.c3Order).count();
    }

    public setOrder(order: C3Order): Observable<void> {
        return this.appwrite.set(Collection.c3Order, order.id, order);
    }

    public deleteOrder(orderId: string): Observable<void> {
        return from(this.appwrite.collection<C3Order>(Collection.c3Order).doc(orderId).delete());
    }

    public loadOrderedProducts(): Observable<C3OrderedProduct[]> {
        return this.appwrite.collection<C3OrderedProduct>(Collection.c3OrderedProduct).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public watchOrderedProducts(): Observable<C3OrderedProduct[]> {
        return this.appwrite.collection<C3OrderedProduct>(Collection.c3OrderedProduct).valueChanges();
    }

    public loadOrderedProductsByOrderIds(orderIds: string[]): Observable<C3OrderedProduct[]> {
        return this.appwrite.listByFieldValues<C3OrderedProduct>(
            Collection.c3OrderedProduct,
            'c3OrderId',
            orderIds,
        ).pipe(
            map((orderedProducts) => this.sortOrderedProductsByCreatedOn(orderedProducts)),
        );
    }

    public watchOrderedProductsByOrderId(orderId: string): Observable<C3OrderedProduct[]> {
        return this.appwrite.collection<C3OrderedProduct>(
            Collection.c3OrderedProduct,
            ref => ref.where('c3OrderId', '==', orderId),
        ).valueChanges().pipe(
            map((orderedProducts) => this.sortOrderedProductsByCreatedOn(orderedProducts)),
        );
    }

    public loadOrderedProductsByProductId(productId: string): Observable<C3OrderedProduct[]> {
        return this.appwrite.collection<C3OrderedProduct>(
            Collection.c3OrderedProduct,
            ref => ref.where('productId', '==', productId),
        ).get().pipe(
            map((resp) => this.sortOrderedProductsByCreatedOn(fromAppwriteDocs(resp.docs))),
        );
    }

    public setOrderedProduct(orderedProduct: C3OrderedProduct): Observable<void> {
        return this.appwrite.set(Collection.c3OrderedProduct, orderedProduct.id, orderedProduct);
    }

    public setOrderedProducts(orderedProducts: C3OrderedProduct[]): Observable<void> {
        if (!orderedProducts.length) {
            return of(void 0);
        }

        return forkJoin(orderedProducts.map((orderedProduct) => this.setOrderedProduct(orderedProduct))).pipe(
            map(() => void 0),
        );
    }

    public deleteOrderedProduct(orderedProductId: string): Observable<void> {
        return this.appwrite.delete(Collection.c3OrderedProduct, orderedProductId);
    }

    private sortOrderedProductsByCreatedOn(orderedProducts: C3OrderedProduct[]): C3OrderedProduct[] {
        return [...orderedProducts].sort((a, b) => (a.createdOn?.getTime() ?? 0) - (b.createdOn?.getTime() ?? 0));
    }


}
